/**
 * React Query hooks for CV operations.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import cvService from '../services/cv.service';

interface UseCVListQueryParams {
  workspaceId: string | undefined;
  idToken: string | undefined;
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  poolIds?: string[];
  positionIds?: string[];
  jobIds?: string[];
  processingStatus?: string;
  search?: string;
  excludeJobId?: string;
  cvTagSlugs?: string[];
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const useCVListQuery = ({
  workspaceId,
  idToken,
  page = 1,
  size = 10,
  sortBy = 'created_at',
  order = 'desc',
  poolIds,
  positionIds,
  jobIds,
  processingStatus,
  search,
  excludeJobId,
  cvTagSlugs,
  enabled = true,
  refetchInterval = 5000,
}: UseCVListQueryParams) => {
  return useQuery({
    queryKey: [
      'cvs',
      workspaceId,
      page,
      size,
      sortBy,
      order,
      poolIds?.sort().join(','),
      positionIds?.sort().join(','),
      jobIds?.sort().join(','),
      processingStatus,
      search,
      excludeJobId,
      cvTagSlugs?.sort().join(','),
    ],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }

      return await cvService.listCVs(workspaceId, idToken, {
        page,
        size,
        sortBy,
        order,
        poolIds,
        positionIds,
        jobIds,
        processingStatus,
        search,
        excludeJobId,
        cvTagSlugs,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    refetchInterval,
    refetchIntervalInBackground: true,
    placeholderData: keepPreviousData,
    staleTime: 2000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UseCVDetailQueryParams {
  cvId: string | undefined;
  workspaceId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
}

export const useCVDetailQuery = ({
  cvId,
  workspaceId,
  idToken,
  enabled = true,
}: UseCVDetailQueryParams) => {
  return useQuery({
    queryKey: ['cv', cvId],
    queryFn: async () => {
      if (!cvId || !workspaceId || !idToken) {
        throw new Error('CV ID, Workspace ID, and token are required');
      }

      return await cvService.getCV(cvId, workspaceId, idToken);
    },
    enabled: enabled && !!cvId && !!workspaceId && !!idToken,
    staleTime: 30000,
    gcTime: 10 * 60 * 1000,
  });
};

interface UseTriggerParseMutationParams {
  cvId: string;
  workspaceId: string | undefined;
  idToken: string | undefined;
}

export const useTriggerParseMutation = ({
  cvId,
  workspaceId,
  idToken,
}: UseTriggerParseMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (attachmentId: string) => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }
      return cvService.triggerParseAttachment(cvId, attachmentId, workspaceId, idToken);
    },
    onSuccess: () => {
      // Invalidate CV detail to refresh status
      queryClient.invalidateQueries({ queryKey: ['cv', cvId] });
      // Invalidate signed URL cache so preview_available is re-fetched
      queryClient.invalidateQueries({ queryKey: ['cv-attachment-signed-url'] });
    },
  });
};

interface UseAttachmentSignedUrlQueryParams {
  cvId: string | undefined;
  attachmentId: string | undefined;
  workspaceId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
  preview?: boolean;
}

interface UseDeleteCVMutationParams {
  workspaceId: string | undefined;
  idToken: string | undefined;
}

export const useDeleteCVMutation = ({ workspaceId, idToken }: UseDeleteCVMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cvId: string) => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }
      return cvService.deleteCV(cvId, workspaceId, idToken);
    },
    onSuccess: (_data, cvId) => {
      queryClient.removeQueries({ queryKey: ['cv', cvId] });
      queryClient.invalidateQueries({ queryKey: ['cvs'] });
    },
  });
};

export const useAttachmentSignedUrlQuery = ({
  cvId,
  attachmentId,
  workspaceId,
  idToken,
  enabled = true,
  preview,
}: UseAttachmentSignedUrlQueryParams) => {
  return useQuery({
    queryKey: ['cv-attachment-signed-url', cvId, attachmentId, preview],
    queryFn: async () => {
      if (!cvId || !attachmentId || !workspaceId || !idToken) {
        throw new Error('CV ID, Attachment ID, Workspace ID, and token are required');
      }

      return await cvService.getAttachmentSignedUrl(cvId, attachmentId, workspaceId, idToken, preview);
    },
    enabled: enabled && !!cvId && !!attachmentId && !!workspaceId && !!idToken,
    // Short staleTime when preview not yet available so it re-fetches quickly;
    // normal 50-min staleTime once we have a valid signed URL
    staleTime: (query) => {
      const data = query.state.data;
      if (data && 'preview_available' in data && data.preview_available === false) {
        return 5000;
      }
      return 50 * 60 * 1000;
    },
    gcTime: 60 * 60 * 1000,
  });
};
